import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaTimes, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { getIconUrl, useAccount, useDoubleSidebar } from "@vojomo-core/shared";
import "./MobileNavigation.module.css";

export interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
  mainLinksData: {
    iconPath: string;
    title: string;
  }[];
  linksData: any;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  isOpen,
  onClose,
  mainLinksData,
  linksData
}) => {
  const { expandedLabels, setExpandedLabels } = useDoubleSidebar();
  const { vojomoAccount, fullLogout } = useAccount();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const userName = vojomoAccount.name.split(" ");
  const initials = userName
    .map((name) => name.charAt(0))
    .join("")
    .toUpperCase();

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const toggleLabel = (labelTitle: string) => {
    setExpandedLabels((prev) => ({
      ...prev,
      [labelTitle]: !prev[labelTitle]
    }));
  };

  const renderMainSection = (section: { iconPath: string; title: string }) => {
    const subLinks = linksData[section.title] || [];
    const isExpanded = expandedSections[section.title];

    // Group links by their parent labels
    const groupedLinks: Record<string, typeof subLinks> = {};
    const topLevelLinks: typeof subLinks = [];

    // First pass: identify all labels
    subLinks.forEach((link: any) => {
      if (link?.isLabel === true) {
        groupedLinks[link.title] = [];
      }
    });

    // Second pass: group links by their explicit parent IDs
    subLinks.forEach((link: any) => {
      if (link?.isLabel === true) {
        topLevelLinks.push(link);
      } else if (link.parentId && groupedLinks[link.parentId]) {
        groupedLinks[link.parentId].push(link);
      } else {
        topLevelLinks.push(link);
      }
    });

    return (
      <div key={section.title} className="mx-3 my-2">
        <button
          onClick={() => toggleSection(section.title)}
          className="w-full flex items-center justify-between p-3 rounded-xl border-2 border-[#2c1561]/10 bg-white hover:border-[#2c1561]/30 hover:shadow-md transition-all duration-200 min-h-touch"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
              <img
                src={getIconUrl(section.iconPath)}
                alt={section.title}
                className="w-5 h-5"
              />
            </div>
            <span className="font-semibold text-[#2c1561] text-base">
              {section.title}
            </span>
          </div>
          {isExpanded ? (
            <FaChevronDown className="text-[#2c1561] text-sm" />
          ) : (
            <FaChevronRight className="text-gray-400 text-sm" />
          )}
        </button>

        {isExpanded && (
          <div className="mt-2 px-3 pb-2 space-y-1">
            {topLevelLinks.map((link: any) => {
              if (link?.isLabel === true) {
                const isLabelExpanded = expandedLabels[link.title] === true;
                const childLinks = groupedLinks[link.title] || [];

                return (
                  <div key={link.title} className="mb-2">
                    <button
                      onClick={() => toggleLabel(link.title)}
                      className="w-full flex items-center justify-between py-2 px-1 transition-all min-h-touch"
                      style={{
                        backgroundColor: "white",
                        border: "none"
                      }}
                    >
                      <span className="font-bold text-sm text-[#2c1561]">
                        {link.label}
                      </span>
                      {isLabelExpanded ? (
                        <FaChevronDown className="text-[#2c1561] text-xs" />
                      ) : (
                        <FaChevronRight className="text-gray-400 text-xs" />
                      )}
                    </button>

                    {isLabelExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {childLinks.map((childLink: any) =>
                          childLink.onClick ? (
                            <div
                              key={childLink.title}
                              onClick={() => {
                                childLink.onClick?.();
                                onClose();
                              }}
                              className="py-2.5 px-3 text-sm text-gray-700 hover:text-[#2c1561] cursor-pointer transition-all min-h-touch flex items-center"
                            >
                              {childLink.label}
                            </div>
                          ) : (
                            <Link
                              key={childLink.title}
                              to={childLink.path}
                              onClick={onClose}
                              className="block py-2.5 px-3 text-sm text-gray-700 hover:text-[#2c1561] transition-all min-h-touch flex items-center no-underline"
                            >
                              {childLink.label}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              } else {
                return link.onClick ? (
                  <div
                    key={link.title}
                    onClick={() => {
                      link.onClick?.();
                      onClose();
                    }}
                    className="py-2.5 px-3 text-sm text-gray-700 bg-white border border-gray-200 hover:border-[#2c1561]/30 hover:bg-[#eeddf7]/20 rounded-lg cursor-pointer transition-all min-h-touch flex items-center"
                  >
                    {link.label}
                  </div>
                ) : (
                  <Link
                    key={link.title}
                    to={link.path}
                    onClick={onClose}
                    className="block py-2.5 px-3 text-sm text-gray-700 bg-white border border-gray-200 hover:border-[#2c1561]/30 hover:bg-[#eeddf7]/20 rounded-lg transition-all min-h-touch flex items-center no-underline"
                  >
                    {link.label}
                  </Link>
                );
              }
            })}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop with smooth fade */}
      <div
        className="fixed inset-0 bg-black z-[999] transition-opacity duration-300"
        style={{
          opacity: isOpen ? 1 : 0
        }}
        onClick={onClose}
      />

      {/* Drawer with Vojomo styling */}
      <div
        className="fixed inset-y-0 left-0 w-80 max-w-[85vw] bg-white z-[1000] shadow-2xl overflow-y-auto"
        style={{
          transform: isOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "4px 0 24px rgba(44, 21, 97, 0.15)",
          backgroundColor: "white"
        }}
      >
        {/* Header with Vojomo branding */}
        <div
          className="sticky top-0 bg-white z-10 shadow-sm border-b border-gray-200"
          style={{ backgroundColor: "white" }}
        >
          <div className="flex items-center justify-between p-4">
            <Link to="/" onClick={onClose} className="flex-1">
              <img
                src={
                  new URL(
                    "../../../static/Vojomo_Horiz_fullcolor_rgb.png",
                    import.meta.url
                  ).href
                }
                alt="Vojomo"
                className="h-8"
              />
            </Link>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors min-w-touch min-h-touch flex items-center justify-center"
              aria-label="Close menu"
            >
              <FaTimes className="text-gray-600" size={20} />
            </button>
          </div>
        </div>

        {/* Navigation sections */}
        <div className="py-2">
          {mainLinksData
            .filter((item) => item.title !== "Vojomo")
            .map((section) => renderMainSection(section))}
        </div>

        {/* Footer with Vojomo branding */}
        <div
          className="sticky bottom-0 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]"
          style={{ backgroundColor: "white" }}
        >
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/account"
              onClick={onClose}
              className="flex items-center gap-3 hover:bg-[#eeddf7]/40 p-3 rounded-xl border border-transparent hover:border-[#2c1561]/20 flex-1 transition-all duration-200 min-h-touch no-underline"
            >
              <div className="w-11 h-11 bg-white border border-[#2c1561]/20 text-[#2c1561] rounded-full flex items-center justify-center font-bold text-base shadow-md">
                {initials[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#2c1561] truncate">
                  {vojomoAccount.name}
                </div>
                <div className="text-xs text-gray-500">My Account</div>
              </div>
            </Link>
            <button
              onClick={() => {
                fullLogout();
                onClose();
              }}
              className="p-3 hover:bg-red-50 rounded-xl border border-transparent hover:border-red-200 transition-all duration-200 min-w-touch min-h-touch flex items-center justify-center"
              aria-label="Logout"
            >
              <img
                src={getIconUrl("icon_log_out")}
                alt="Logout"
                className="w-5 h-5 opacity-60 hover:opacity-100 transition-opacity"
              />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
